<template>
    <div class="flex flex-col items-start gap-1 md:flex-row md:items-center">
        <div class="text-lg font-semibold">
            {{ propertyData.originalQuestionText }}
        </div>
        <div
            v-if="enrollment !== null"
            class="italic md:not-italic"
        >
            <span class="hidden md:inline">&mdash;&nbsp;</span>{{ enrollment }}
        </div>
    </div>
    <div
        v-if="answer === ''"
        class="text-gray-500"
    >
        &mdash;
    </div>
    <div
        v-else
        class="flex gap-2 whitespace-pre"
    >
        <div>{{ answer }}</div>
        <div
            v-if="feePricing !== ''"
            class="text-gray-600"
        >
            {{ feePricing }}
        </div>
        <div
            v-if="duesPricing !== ''"
            class="text-gray-600"
        >
            {{ duesPricing }}
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const ContractPropertyDisplayFragment = graphql(`
    fragment ContractPropertyDisplayFragment on ContractProperty {
        id
        originalQuestionText
        value
        planQuestionOptions {
            translatedLabel(language: $language)
            pricingConfig {
                duesPricingTotal
                feePricingTotal
            }
        }
        planQuestion {
            translatedConfig(language: $language) {
                __typename
                ... on PlanNumberQuestion {
                    unitPricing {
                        duesPricingTotal
                        feePricingTotal
                    }
                }
            }
        }
        enrollment {
            customer {
                commaName
            }
        }
        contract {
            __typename
            ... on PlanContract {
                plan {
                    terms {
                        billingType
                        billingInterval
                        billingCycleString(language: $language)
                    }
                }
            }
        }
    }
`)
</script>

<script setup lang="ts">
import { type PropType } from 'vue'
import { computed } from 'vue'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import { Decimal } from '~/composables/support/decimal'
import { formatRecurringTotalString } from '~/composables/contract-question-formatting'
import { useI18n } from 'vue-i18n'

const props = defineProps({
    property: {
        type: Object as PropType<
            FragmentType<typeof ContractPropertyDisplayFragment>
        >,
        required: true
    }
})

const { t } = useI18n({ useScope: 'global' })
const propertyData = getFragmentData(
    ContractPropertyDisplayFragment,
    props.property
)

const enrollment = computed(() => {
    if (propertyData.enrollment === null) {
        return null
    } else {
        return propertyData.enrollment.customer.commaName
    }
})

const billingInterval = computed(() => {
    if (propertyData.contract.__typename !== 'PlanContract') {
        throw new Error()
    }

    // If trying to access billing interval on a non-recurring contract something went wrong
    if (propertyData.contract.plan.terms.billingType !== 'RECURRING') {
        throw new Error()
    }

    return propertyData.contract.plan.terms.billingInterval
})

const answer = computed(() => {
    // If the plan question is one of these basic types, just return the value
    if (
        // TODO: ensure that this is how we want to handle custom properties
        propertyData.planQuestion === null ||
        propertyData.planQuestion.translatedConfig.__typename ===
            'PlanTextQuestion' ||
        propertyData.planQuestion.translatedConfig.__typename ===
            'PlanParagraphQuestion' ||
        propertyData.planQuestion.translatedConfig.__typename ===
            'PlanNumberQuestion' ||
        propertyData.planQuestion.translatedConfig.__typename ===
            'PlanPhoneNumberQuestion' ||
        propertyData.planQuestion.translatedConfig.__typename ===
            'PlanEmailQuestion'
    ) {
        return propertyData.value
    }
    // Else if the plan question is a boolean type, show the answer as yes or no
    else if (
        propertyData.planQuestion.translatedConfig.__typename ===
        'PlanBooleanQuestion'
    ) {
        if (propertyData.value === '1') {
            return t('yes')
        } else {
            return t('no')
        }
    }
    // Else this must be a plan single choice or multiple choice question
    else {
        let optionDisplay = ''
        propertyData.planQuestionOptions.forEach(option => {
            optionDisplay += option.translatedLabel + '\n'
        })
        return optionDisplay
    }
})

const duesPricing = computed(() => {
    // TODO: not handling pricing for custom properties yet
    if (propertyData.planQuestion === null) {
        return ''
    }

    // If this is for a number question
    if (
        propertyData.planQuestion.translatedConfig.__typename ===
        'PlanNumberQuestion'
    ) {
        // If there is no pricing return empty
        if (propertyData.planQuestion.translatedConfig.unitPricing === null) {
            return ''
        }
        let numberPricing = ''
        const value = new Decimal(propertyData.value)

        const duesPricing = new Decimal(
            propertyData.planQuestion.translatedConfig.unitPricing.duesPricingTotal
        )
        // If there is dues pricing, show the total amount per billing interval that will be added
        if (duesPricing.isNotZero()) {
            const total = value.multipliedBy(duesPricing)
            numberPricing +=
                ' +' +
                formatRecurringTotalString(
                    total,
                    billingInterval.value,
                    propertyData.contract.plan.terms.billingCycleString
                )
        }
        return numberPricing
    }
    // Else if this is for a single choice or multiple choice question
    else if (
        propertyData.planQuestion.translatedConfig.__typename ===
            'PlanSingleChoiceQuestion' ||
        propertyData.planQuestion.translatedConfig.__typename ===
            'PlanMultipleChoiceQuestion'
    ) {
        let optionDisplay = ''
        propertyData.planQuestionOptions.forEach(option => {
            const duesPricing = new Decimal(
                option.pricingConfig.duesPricingTotal
            )
            // If there is dues pricing show the amount per billing interval this option added
            if (duesPricing.isNotZero()) {
                optionDisplay +=
                    ' +' +
                    formatRecurringTotalString(
                        duesPricing,
                        billingInterval.value,
                        propertyData.contract.plan.terms.billingCycleString
                    ) +
                    '\n'
            } else {
                optionDisplay += '\n'
            }
        })
        return optionDisplay
    }
    return ''
})

const feePricing = computed(() => {
    // TODO: not handling pricing for custom properties yet
    if (propertyData.planQuestion === null) {
        return ''
    }

    // If this is for a number question
    if (
        propertyData.planQuestion.translatedConfig.__typename ===
        'PlanNumberQuestion'
    ) {
        // If there is no pricing return empty
        if (propertyData.planQuestion.translatedConfig.unitPricing === null) {
            return ''
        }

        let numberPricing = ''
        const value = new Decimal(propertyData.value)

        const feePricing = new Decimal(
            propertyData.planQuestion.translatedConfig.unitPricing.feePricingTotal
        )
        // If there is fee pricing, show the total amount that has been added
        if (feePricing.isNotZero()) {
            const total = value.multipliedBy(feePricing)
            numberPricing += ' +' + total.formatCurrency()
        }
        return numberPricing
    }
    // Else if this is for a single choice or multiple choice question
    else if (
        propertyData.planQuestion.translatedConfig.__typename ===
            'PlanSingleChoiceQuestion' ||
        propertyData.planQuestion.translatedConfig.__typename ===
            'PlanMultipleChoiceQuestion'
    ) {
        let optionDisplay = ''
        propertyData.planQuestionOptions.forEach(option => {
            const feePricing = new Decimal(option.pricingConfig.feePricingTotal)
            // If there is fee pricing show the amount this option added
            if (feePricing.isNotZero()) {
                optionDisplay += ' +' + feePricing.formatCurrency() + '\n'
            } else {
                optionDisplay += '\n'
            }
        })
        return optionDisplay
    }
    return ''
})
</script>
